// ADR 17.10.26g §4/§6 — where a tip points, and how it is measured.
//
// A pure-TS registry: a target component calls `useCoachAnchor(id)` and puts
// the returned ref on its element. That element is also stamped
// `data-coach="<id>"`, a debugging and e2e aid only — the registry is the
// authority. This file is on the boot-safe side of the folder (§3, AC16): its
// one import from outside is `motion/useReducedMotion.ts`, a pure hook, so a
// boot-graph component (C3's `HeaderSwitcher`) may carry an anchor.
//
// Measurement is `getBoundingClientRect()` in viewport space, the one space
// 8.8.26c argued for, and a zero-width or zero-height rect is no target at all
// (9.8.26e). `useCoachAnchor` never reads the window class: a record anchor is
// resolved by the host with the whole-window `navChrome` (§6).
//
// While a tip is presented its targets are measured every animation frame
// (19.10.26b): no listener set can name every reflow that moves an element
// without resizing it (the dock's lazy track, the sidebar, a banner), and a
// frame loop needs none of them.

import { useEffect, useMemo, useRef, useState, type RefCallback } from 'react'

import { useReducedMotion } from '../../motion/useReducedMotion'
import type { NavChrome } from '../../wasm'
import type { CoachStep } from './coachMachine'

export interface AnchorRect {
  top: number
  left: number
  width: number
  height: number
}

const elements = new Map<string, HTMLElement>()
const registryListeners = new Set<() => void>()

function notifyRegistry(): void {
  for (const listener of [...registryListeners]) listener()
}

/**
 * Called on every set and delete: `useAnchorRects`' wake-up when a target
 * mounts or unmounts, and 17.10.26i §3's — the tour counts an arriving
 * group's targets with it, and notices the gear becoming the switcher.
 */
export function subscribeAnchors(listener: () => void): () => void {
  registryListeners.add(listener)
  return () => {
    registryListeners.delete(listener)
  }
}

/**
 * Put on the target element.
 *
 * Several sites may share one id (17.10.26h §3: one id, two exclusive sites),
 * so a detach is identity-checked: each callback remembers the element it
 * registered and, on `null`, deletes the id only while the registry still
 * holds that element. Whichever site attached last wins, in either order.
 */
export function useCoachAnchor<T extends HTMLElement>(id: string): RefCallback<T> {
  return useMemo(() => {
    let mine: T | null = null
    return (element: T | null) => {
      if (element === null) {
        if (mine !== null && elements.get(id) === mine) elements.delete(id)
        mine = null
      } else {
        mine = element
        elements.set(id, element)
        element.dataset.coach = id
      }
      notifyRegistry()
    }
  }, [id])
}

/** A record anchor's id for the chrome actually drawn; a string anchor unchanged. */
export function resolveAnchorId(anchor: CoachStep['anchor'], navChrome: NavChrome): string {
  return typeof anchor === 'string' ? anchor : anchor[navChrome]
}

/** The target's viewport rect, or `null` when it is unregistered or has no area. */
export function measureAnchor(id: string): AnchorRect | null {
  const element = elements.get(id)
  if (element === undefined) return null
  const { top, left, width, height } = element.getBoundingClientRect()
  return width > 0 && height > 0 ? { top, left, width, height } : null
}

const measureAll = (ids: readonly string[]) => ids.map(measureAnchor)

/** One rect compare for C1's loop and the glide's start read (19.10.26c §3). */
export const sameRect = (a: AnchorRect | null, b: AnchorRect | null) =>
  a === b ||
  (a !== null && b !== null && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height)

const sameRects = (a: readonly (AnchorRect | null)[], b: readonly (AnchorRect | null)[]) =>
  a.length === b.length && a.every((rect, i) => sameRect(rect, b[i]))

/**
 * The frame clock (19.10.26b §3): structurally the `raf`/`caf` half of
 * 17.10.26f's `ClockDeps`, declared here because `motion/useLoopClock.ts` is
 * off the eager-entry graph this file is on (17.10.26i AC15).
 */
export interface FrameDeps {
  raf: (callback: FrameRequestCallback) => number
  caf: (handle: number) => void
}

// Read `window` at call time: the suites stub it after this module has loaded.
const defaultFrameDeps: FrameDeps = {
  raf: (cb) => window.requestAnimationFrame(cb),
  caf: (id) => window.cancelAnimationFrame(id),
}

/**
 * Every id's rect, re-measured once per animation frame for as long as the
 * caller is mounted (19.10.26b §1–§2) — so a target an ancestor's reflow moves
 * without resizing it is followed too. State is set only on a changed rect; a
 * registry change is the immediate wake-up when a target mounts or unmounts.
 */
export function useAnchorRects(ids: readonly string[], deps?: Partial<FrameDeps>): (AnchorRect | null)[] {
  const key = ids.join('\n')
  const [rects, setRects] = useState(() => measureAll(ids))
  // The last committed rects, seeded with what the first render drew.
  const shown = useRef(rects)
  const raf = deps?.raf ?? defaultFrameDeps.raf
  const caf = deps?.caf ?? defaultFrameDeps.caf
  useEffect(() => {
    const list = key.split('\n')
    let frame: number | null = null
    // A synchronous `raf` (coachHarness.tsx's stub) would otherwise recurse.
    let inRequest = false
    const commitIfChanged = () => {
      const next = measureAll(list)
      if (sameRects(next, shown.current)) return
      shown.current = next
      setRects(next)
    }
    const tick = () => {
      if (inRequest) return
      commitIfChanged()
      inRequest = true
      frame = raf(tick)
      inRequest = false
    }
    tick()
    const stopRegistry = subscribeAnchors(commitIfChanged)
    return () => {
      if (frame !== null) caf(frame)
      stopRegistry()
    }
  }, [key, raf, caf])
  return rects
}

const offScreen = (rect: DOMRect) => rect.bottom < 0 || rect.top > window.innerHeight || rect.right < 0 || rect.left > window.innerWidth

/** Before a step is shown, bring an off-screen target into view (smoothly unless Reduce Motion). */
export function useRevealAnchor(id: string | null): void {
  const reduced = useReducedMotion()
  useEffect(() => {
    const element = id === null ? undefined : elements.get(id)
    if (element === undefined || typeof element.scrollIntoView !== 'function') return
    if (!offScreen(element.getBoundingClientRect())) return
    element.scrollIntoView({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' })
  }, [id, reduced])
}
